//@ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Box,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAddProduct,
  useUpdateProduct,
  useProductDetail,
  useGetProductUnits,
  useGetTaxRates,
  useGetProductGroups
} from '../../../hooks';
import { useSetSnackBar } from '../../../store/Snackbar.state';
import { AppCard, Loading } from '../../atoms';

type ProductFormData = any;

function ProductForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const setSnackbar = useSetSnackBar();

  const isEditMode = Boolean(id);

  const { mutate: addProduct, isLoading: isAddProductLoading } = useAddProduct();
  const { mutate: updateProduct, isLoading: isUpdateLoading } = useUpdateProduct();
  const { mutate: getProductDetail, isLoading: isDetailLoading } = useProductDetail();
  const { data: productUnitsData, isLoading: isProductUnitsLoading } = useGetProductUnits();
  const { data: taxRatesData, isLoading: isTaxRatesLoading } = useGetTaxRates();
  const { data: productGroupsData, isLoading: isProductGroupsLoading } = useGetProductGroups({ status: "ACTIVE" });

  const isLoading = isAddProductLoading || isUpdateLoading || isDetailLoading || isProductUnitsLoading || isTaxRatesLoading || isProductGroupsLoading;

  const [formData, setFormData] = useState<ProductFormData>({
    productCode: '',
    productName: '',
    productGroupId: '',
    salePrice: '',
    buyingPrice: null,
    taxRateId: '',
    unit: '',
    merchantId: 0,
    currencyCode: '949',
    isOpenPrice: false,
    barcode: null,
    status: 'ACTIVE',
  });

  useEffect(() => {
    if (isEditMode && id) {
      getProductDetail({ id: parseInt(id, 10) }, {
        onSuccess: (response) => {
          if (response.isSuccess && response.data) {
            setFormData({
              ...response.data,
              salePrice: response.data.salePrice || '',
            });
          } else {
            setSnackbar({
              severity: 'error',
              description: response.message || 'Ürün detayı alınamadı',
              isOpen: true,
            });
          }
        },
        onError: () => {
          setSnackbar({
            severity: 'error',
            description: 'Ürün detayı alınırken bir hata oluştu',
            isOpen: true,
          });
        }
      });
    }
  }, [id, isEditMode, getProductDetail, setSnackbar]);

  const handleInputChange = (field: keyof ProductFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field: keyof ProductFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: event.target.checked }));
  };

  const handleSelectChange = (field: keyof ProductFormData) => (
    event: any
  ) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = () => {
    if (!formData.productName || !formData.productGroupId || !formData.taxRateId || !formData.unit) {
      setSnackbar({
        severity: 'error',
        description: 'Zorunlu alanlar doldurulmalıdır',
        isOpen: true,
      });
      return;
    }

    const mutation = isEditMode ? updateProduct : addProduct;
    const successMessage = isEditMode ? 'Ürün başarıyla güncellendi!' : 'Ürün başarıyla eklendi!';
    const errorMessage = isEditMode ? 'Ürün güncellenirken bir hata oluştu' : 'Ürün eklenirken bir hata oluştu';

    mutation(formData, {
      onSuccess: (data) => {
        if (data.isSuccess) {
          setSnackbar({
            severity: 'success',
            isOpen: true,
            description: data.message || successMessage,
          });
          navigate('/vuk-507/products', { state: { refresh: true } });
        } else {
          setSnackbar({
            severity: 'error',
            description: data.message || errorMessage,
            isOpen: true,
          });
        }
      },
      onError: (error) => {
        setSnackbar({
          severity: 'error',
          description: 'İşlem sırasında bir hata oluştu',
          isOpen: true,
        });
      }
    });
  };

  return (
    <>
      {isLoading && <Loading />}
      <Box p={2}>
        <AppCard>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Stok Kodu"
                  value={formData.productCode}
                  onChange={handleInputChange('productCode')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Ürün Adı"
                  value={formData.productName}
                  onChange={handleInputChange('productName')}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Ürün Grubu</InputLabel>
                  <Select
                    value={formData.productGroupId}
                    label="Ürün Grubu"
                    onChange={handleSelectChange('productGroupId')}
                    disabled={isProductGroupsLoading}
                  >
                    {isProductGroupsLoading ? (
                      <MenuItem disabled>Yükleniyor...</MenuItem>
                    ) : (
                      productGroupsData?.data?.result?.map((productGroup: any) => (
                        <MenuItem key={productGroup.id} value={productGroup.id}>
                          {productGroup.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Satış Fiyatı"
                  type="number"
                  inputProps={{ step: 0.01 }}
                  value={formData.salePrice}
                  onChange={handleInputChange('salePrice')}
                  required={!formData.isOpenPrice}
                  disabled={formData.isOpenPrice}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Vergi Oranı</InputLabel>
                  <Select
                    value={formData.taxRateId}
                    label="Vergi Oranı"
                    onChange={handleSelectChange('taxRateId')}
                    disabled={isTaxRatesLoading}
                  >
                    {isTaxRatesLoading ? (
                      <MenuItem disabled>Yükleniyor...</MenuItem>
                    ) : (
                      [...(taxRatesData?.data?.result ?? [])]
                        .sort((a, b) => {
                          if (a.merchantId === 0 && b.merchantId !== 0) return -1;
                          if (a.merchantId !== 0 && b.merchantId === 0) return 1;
                          return Number(a.tax) - Number(b.tax);
                        })
                        .map((taxRate: any) => (
                          <MenuItem key={taxRate.id} value={taxRate.id}>
                            {taxRate.name} (%{taxRate.tax})
                          </MenuItem>
                        ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Birim</InputLabel>
                  <Select
                    value={formData.unit}
                    label="Birim"
                    onChange={handleSelectChange('unit')}
                    disabled={isProductUnitsLoading}
                  >
                    {productUnitsData?.data?.map((unit: any) => (
                      <MenuItem key={unit.id} value={unit.id}>
                        {unit.name} ({unit.shortCode})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Para Birimi</InputLabel>
                  <Select
                    value={formData.currencyCode}
                    label="Para Birimi"
                    onChange={handleSelectChange('currencyCode')}
                  >
                    <MenuItem value="949">TL</MenuItem>
                    <MenuItem value="840">USD</MenuItem>
                    <MenuItem value="978">EUR</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Durum</InputLabel>
                  <Select
                    value={formData.status}
                    label="Durum"
                    onChange={handleSelectChange('status')}
                  >
                    <MenuItem value="ACTIVE">Aktif</MenuItem>
                    <MenuItem value="PASSIVE">Pasif</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isOpenPrice}
                      onChange={handleCheckboxChange('isOpenPrice')}
                    />
                  }
                  label="Açık Fiyat"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(-1)}
                  >
                    İptal
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={isLoading}
                  >
                    Kaydet
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </AppCard>
      </Box>
    </>
  );
}

export default ProductForm;
